/**
 * The AI token tracker module's registration.
 *
 * Everything the platform needs to know about this module is here: its navigation, its
 * routes, and which roles reach which screen. The shell reads this; it knows nothing else
 * about the module, which is what lets a second module be added without touching it.
 *
 * Screens are lazily imported so a developer landing on their own usage does not download
 * the approvals queue, the audit log, and the chart code they will never open.
 */

import { lazy } from 'react'

import { PATHS } from '../../platform/routing/paths'
import type { ModuleDefinition } from '../../platform/modules/registry'

const MyUsageScreen = lazy(() => import('./screens/my-usage/MyUsageScreen'))
const OverviewScreen = lazy(() => import('./screens/overview/OverviewScreen'))
const AllocationsScreen = lazy(
  () => import('./screens/allocations/AllocationsScreen'),
)
const AllocationEditorRoute = lazy(
  () => import('./screens/allocations/AllocationEditorRoute'),
)
const ApprovalsScreen = lazy(
  () => import('./screens/approvals/ApprovalsScreen'),
)
const AuditLogScreen = lazy(() => import('./screens/audit/AuditLogScreen'))
const ApprovalActionScreen = lazy(
  () => import('./screens/approval-actions/ApprovalActionScreen'),
)

export const aiTokenTrackerModule: ModuleDefinition = {
  id: 'ai-token-tracker',
  title: 'Devsphere',
  nav: {
    items: [
      // Order matters twice over: it is the navigation order, and the first item a user's
      // roles permit becomes their landing page. A developer therefore lands on their own
      // usage and a manager on the overview, without either being special-cased.
      {
        id: 'overview',
        label: 'Overview',
        to: PATHS.overview,
        iconName: 'wk-icon-chart',
        requiredRoles: [
          'manager',
          'governance-admin',
          'platform-admin',
          'executive',
        ],
      },
      {
        id: 'my-usage',
        label: 'My usage',
        to: PATHS.myUsage,
        iconName: 'wk-icon-user',
      },
      {
        id: 'allocations',
        label: 'Users and allocations',
        to: PATHS.allocations,
        iconName: 'wk-icon-users',
        requiredRoles: ['manager', 'governance-admin', 'platform-admin'],
      },
      {
        id: 'approvals',
        label: 'Approvals',
        to: PATHS.approvals,
        iconName: 'wk-icon-check-circle',
        requiredRoles: ['manager', 'governance-admin', 'platform-admin'],
      },
      {
        id: 'audit',
        label: 'Audit log',
        to: PATHS.auditLog,
        iconName: 'wk-icon-clock-back',
        requiredRoles: ['governance-admin', 'platform-admin'],
      },
    ],
  },
  routes: [
    { path: PATHS.overview, Component: OverviewScreen },
    { path: PATHS.myUsage, Component: MyUsageScreen },
    {
      path: PATHS.allocations,
      Component: AllocationsScreen,
      // Nested, so the editor renders inside the list's outlet. The list keeps its data
      // and its scroll position while the drawer is open, and closing the drawer needs no
      // refetch.
      children: [
        { path: PATHS.allocationEditor, Component: AllocationEditorRoute },
      ],
    },
    { path: PATHS.approvals, Component: ApprovalsScreen },
    { path: PATHS.auditLog, Component: AuditLogScreen },
  ],
  // Outside the authenticated shell entirely -- reachable only from the signed,
  // single-use link an `approval_requested` email carries. See `ApprovalActionScreen`'s
  // own docstring and `registry.publicRoutes`.
  publicRoutes: [
    { path: PATHS.approvalAction, Component: ApprovalActionScreen },
  ],
}
